import * as Sentry from '@sentry/react';
import { SENTRY_FEATURE_TAGS, SENTRY_STAGE_TAGS } from '@vizit/constants';
import * as React from 'react';
import type { UserView } from '../../types';
import {
  APIErrorType,
  getLocalStorage,
  getOrganization,
  getUser,
  loginUser,
  logoutUser,
  removeLocalStorage,
  setLocalStorage,
  setPassword,
} from '../../utils';
import { APIError } from '../../utils';
import { AuthActionType, type AuthState } from './auth-provider.state';

const checkIsVizitEmail = (email?: string) => !!email && email.trim().toLowerCase().endsWith('@vizit.com');

function useAuthentication(props: {
  authState: AuthState;
  dispatch: React.ActionDispatch<
    [
      action: {
        type: AuthActionType;
        payload?: Partial<AuthState>;
      },
    ]
  >;
}) {
  const { authState, dispatch } = props;

  const handleAuthenticate = React.useCallback(
    async (
      email: string,
      password: string,
      onSuccess?: (data: AuthState) => void | Promise<void>,
      onError?: (error: unknown | APIError) => void,
    ) => {
      let loginData: Awaited<ReturnType<typeof loginUser>>;

      try {
        loginData = await loginUser({
          email_address: email,
          password,
        });
      } catch (error) {
        onError?.(error);
        return;
      }

      const { organization_id, user_id, organization_name, default_portfolio_id } = loginData;
      const isVizitUser = checkIsVizitEmail(email);

      // Fetch organization data to get default_retailer.
      // All authenticated users have organization:read permission on their own org.
      let defaultRetailer: string | undefined;

      try {
        const organizationData = await getOrganization(organization_id);
        defaultRetailer = organizationData?.default_retailer ?? undefined;
      } catch (error) {
        Sentry.captureException(error, {
          level: 'warning',
          tags: {
            feature: SENTRY_FEATURE_TAGS.Authentication,
            stage: SENTRY_STAGE_TAGS[SENTRY_FEATURE_TAGS.Authentication].loginUser,
          },
          extra: { organization_id, user_id },
        });
      }

      // Need to set the organization and user id in localStorage to persist the auth state
      setLocalStorage({
        organization_id,
        user_id,
        organization_name,
        user_email: email,
        default_portfolio_id,
        default_retailer: defaultRetailer,
        banner: isVizitUser ? 'true' : 'false',
      });

      // Update the auth state to reflect the new organization.
      dispatch({
        type: AuthActionType.Authenticate,
        payload: {
          isVizitUser,
          isDisplayingBanner: isVizitUser,
          organizationId: organization_id,
          userId: user_id,
          defaultPortfolioId: default_portfolio_id,
          defaultRetailer,
          organizationName: organization_name,
          userEmail: email,
        },
      });

      // Return the new auth state to the caller.
      await onSuccess?.({
        isAuthenticated: true,
        isVizitUser,
        isDisplayingBanner: isVizitUser,
        organizationId: organization_id,
        userId: user_id,
        defaultPortfolioId: default_portfolio_id,
        defaultRetailer,
        organizationName: organization_name,
        userEmail: email,
      });
    },
    [dispatch],
  );

  const handleRemoveAuthentication = React.useCallback(async () => {
    Sentry.addBreadcrumb({
      message: 'Starting authentication removal.',
      level: 'info',
      data: { user_id: authState.userId, organization_id: authState.organizationId },
    });

    // Remove the local storage values.
    removeLocalStorage([
      'organization_id',
      'user_id',
      'organization_name',
      'user_email',
      'default_portfolio_id',
      'default_retailer',
      'banner',
    ]);

    // Remove the JWT token from cookies on the server.
    if (authState.userId) {
      await logoutUser(authState.userId);
    }

    // Reset the auth state.
    dispatch({
      type: AuthActionType.RemoveAuthentication,
    });

    Sentry.addBreadcrumb({
      message: 'Authentication removed successfully',
      level: 'info',
    });
  }, [dispatch, authState.userId, authState.organizationId]);

  const handleOnReauthenticate = React.useCallback(async () => {
    Sentry.addBreadcrumb({
      message: 'Attempting to reauthenticate user.',
      level: 'info',
      data: { user_id: authState.userId, organization_id: authState.organizationId },
    });

    // Grab local storage values.
    const {
      organization_id: organizationId,
      user_id: userId,
      banner,
    } = getLocalStorage(['organization_id', 'user_id', 'banner']);

    // Check if the user data is missing.
    const isMissingUserData = !authState.organizationName || !authState.userEmail || !authState.defaultPortfolioId;

    // If true, the user has a JWT token within cookies, but the user data is missing.
    // We need to fetch the user data and update the auth state to match.
    if (organizationId && userId && isMissingUserData) {
      try {
        const userData = await getUser(userId);
        const isVizitUser = checkIsVizitEmail(userData?.email_address);

        // All authenticated users have organization:read permission on their own org.
        const organizationData = await getOrganization(organizationId);

        if (userData?.email_address && organizationData?.default_portfolio_id && organizationData?.name) {
          setLocalStorage({
            organization_name: organizationData?.name,
            user_email: userData?.email_address,
            default_portfolio_id: organizationData?.default_portfolio_id,
            default_retailer: organizationData?.default_retailer ?? undefined,
            banner: isVizitUser ? (banner ?? 'true') : 'false',
          });

          dispatch({
            type: AuthActionType.Reauthenticate,
            payload: {
              isVizitUser,
              isDisplayingBanner: isVizitUser ? (banner !== undefined ? banner === 'true' : true) : false,
              organizationId,
              organizationName: organizationData?.name,
              defaultPortfolioId: organizationData?.default_portfolio_id,
              defaultRetailer: organizationData?.default_retailer ?? undefined,
              userEmail: userData?.email_address,
              userId,
            },
          });

          return true;
        }

        return false;
      } catch (error) {
        if (error instanceof APIError && APIErrorType.NotAuthenticated === error.type) {
          Sentry.addBreadcrumb({
            message: 'Removing authentication due to NotAuthenticated error.',
            level: 'warning',
            data: { user_id: userId, organization_id: organizationId },
          });

          // Remove the auth state since we are unable to reauthenticate the user.
          await handleRemoveAuthentication();
        } else {
          Sentry.captureException(error, {
            level: 'warning',
            tags: {
              feature: SENTRY_FEATURE_TAGS.Authentication,
              stage: SENTRY_STAGE_TAGS[SENTRY_FEATURE_TAGS.Authentication].reauthenticate,
            },
            extra: { user_id: userId, organization_id: organizationId },
          });
        }

        return false;
      }
    }

    return false;
  }, [authState, dispatch, handleRemoveAuthentication]);

  const handleSetPassword = React.useCallback(
    async (
      user_id: string,
      token: string,
      new_password: string,
      onSuccess?: (data: AuthState) => void | Promise<void>,
      onError?: (error: unknown | APIError) => void,
    ) => {
      const handleOnSuccess = async (data: UserView) => {
        const { email_address } = data;

        if (email_address) {
          await handleAuthenticate(data.email_address, new_password, onSuccess, (error) => {
            onError?.(error);
          });
        }
      };

      const handleOnError = (error: unknown | APIError) => {
        onError?.(error);
      };

      await setPassword(
        {
          user_id,
          new_password,
          password_reset_code: token.toString(),
        },
        handleOnSuccess,
        handleOnError,
      );
    },
    [handleAuthenticate],
  );

  const handleOnExchangeToken = React.useCallback(
    async (organizationId: string, organizationName: string, portfolioId: string) => {
      Sentry.addBreadcrumb({
        message: 'Attempting token exchange.',
        level: 'info',
        data: { organization_id: organizationId },
      });

      if (organizationId && organizationName && portfolioId) {
        try {
          // Fetch organization data to get default_retailer
          const organizationData = await getOrganization(organizationId);

          setLocalStorage({
            organization_id: organizationId,
            organization_name: organizationName,
            default_portfolio_id: portfolioId,
            default_retailer: organizationData?.default_retailer ?? undefined,
            banner: 'true',
          });

          dispatch({
            type: AuthActionType.ExchangeToken,
            payload: {
              organizationId,
              organizationName,
              defaultPortfolioId: portfolioId,
              defaultRetailer: organizationData?.default_retailer ?? undefined,
            },
          });

          Sentry.addBreadcrumb({
            message: 'Token exchange successful',
            level: 'info',
            data: { organization_id: organizationId },
          });
        } catch (error) {
          Sentry.captureException(error, {
            level: 'error',
            tags: {
              feature: SENTRY_FEATURE_TAGS.Authentication,
              stage: SENTRY_STAGE_TAGS[SENTRY_FEATURE_TAGS.Authentication].exchangeToken,
            },
            extra: { organizationId, portfolioId, error },
          });

          throw error;
        }
      } else {
        Sentry.captureException(new Error('Failed to exchange token'), {
          level: 'error',
          tags: {
            feature: SENTRY_FEATURE_TAGS.Authentication,
            stage: SENTRY_STAGE_TAGS[SENTRY_FEATURE_TAGS.Authentication].exchangeToken,
          },
          extra: { organizationId, portfolioId },
        });

        throw new Error('Failed to exchange token.');
      }
    },
    [dispatch],
  );

  const handleOnOrganizationBannerDismiss = React.useCallback(() => {
    setLocalStorage({ banner: 'false' });

    dispatch({ type: AuthActionType.DismissBanner });
  }, [dispatch]);

  return {
    onAuthenticate: handleAuthenticate,
    onReauthenticate: handleOnReauthenticate,
    onRemoveAuthentication: handleRemoveAuthentication,
    onSetPassword: handleSetPassword,
    onExchangeToken: handleOnExchangeToken,
    onDismissBanner: handleOnOrganizationBannerDismiss,
  };
}

export { useAuthentication, checkIsVizitEmail };
